import { Box, Button, Center, Heading, Link, Text, VStack } from "@chakra-ui/react";
import { ChainRegistryService } from "@umami/chains";
import { DynamicDisclosureProvider, useDynamicModalContext } from "@umami/components";
import { useAppSelector, useCurrentAccount, useImplicitAccounts } from "@umami/state";
import { useMemo } from "react";

import { feedbackEmailBodyTemplate } from "./constants";
import { AlertCircleIcon } from "../../assets/icons";
import { useColor } from "../../styles/useColor";
import { LogoutModal } from "../Menu/LogoutModal";
import { useHandleSaveBackup } from "../Onboarding/ImportWallet/useSaveBackup";

const SaveBackupButton = () => {
  const saveBackup = useHandleSaveBackup();

  return (
    <Button width="100%" onClick={saveBackup} size="lg" variant="primary">
      Save backup
    </Button>
  );
};

const LogOutButton = () => {
  const { openWith: openModal } = useDynamicModalContext();

  return (
    <Button width="full" onClick={() => openModal(<LogoutModal />)} size="lg" variant="alert">
      Sign out
    </Button>
  );
};

export const ErrorPage = () => {
  const color = useColor();
  const defaultAccount = useAppSelector(s => s.accounts.defaultAccount);
  const implicitAccounts = useImplicitAccounts();
  const isLoggedIn = implicitAccounts.length > 0;
  const currentAccount = useCurrentAccount();
  const { configs } = useAppSelector(state => state.networks);

  // Initialize the chain registry service before the app renders
  // TODO: Come up with a better way to initialize the chain registry service
  useMemo(() => {
    ChainRegistryService.initialize(configs, currentAccount?.chains);
  }, [currentAccount?.chains, configs]);

  const onlySocialAccountsExists =
    defaultAccount?.type === "social" &&
    !implicitAccounts.filter(item => item.type !== "social").length;

  return (
    <DynamicDisclosureProvider>
      <Center height="100vh" padding="60px" backgroundSize="cover">
        <Box
          width="480px"
          padding="40px"
          border="1px"
          borderColor={color("100")}
          borderTopRadius="30px"
          borderBottomRadius="30px"
          backgroundColor={color("white")}
        >
          <VStack maxHeight="83vh" spacing={0}>
            <Box marginBottom="16px" color={color("700")}>
              <AlertCircleIcon />
            </Box>
            <Center flexDirection="column" width="340px" marginBottom="32px">
              <Heading color={color("700")} lineHeight="26px" size="xl">
                Oops! Something went wrong!
              </Heading>
              <Text
                marginTop="10px"
                color={color("600")}
                lineHeight="18px"
                textAlign="center"
                size="sm"
              >
                Please refresh the page or use one of the following options:
              </Text>
            </Center>

            <VStack width="100%" spacing="16px">
              {isLoggedIn && !onlySocialAccountsExists && <SaveBackupButton />}
              <Button width="full" size="lg" variant="secondary">
                <Link
                  width="full"
                  _hover={{ textDecoration: "none" }}
                  href={`mailto:umami-support@trili.tech?subject=Umami V2 feedback&body=${feedbackEmailBodyTemplate}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Report error
                </Link>
              </Button>
              {isLoggedIn && <LogOutButton />}
            </VStack>
          </VStack>
        </Box>
      </Center>
    </DynamicDisclosureProvider>
  );
};
